import type { LayoutElement, TrackNodeAnnotation } from '../types';
import { buildTrackGraph, type TrackEdge, type TrackGraph } from './simulationGraph';
import {
  buildSimulationTrackNodeViews,
  simulationTrackNodeId,
} from './simulationTrackNetworkView';
import {
  DEFAULT_GATE_FOUP_CAPACITY,
  SYNTHETIC_ENTRY_LOAD_PORT_ID,
  SYNTHETIC_EXIT_LOAD_PORT_ID,
} from './trackNodeAnnotations';
import { ENDPOINT_TOLERANCE } from './topology';

export type DerivedGateRole = 'entry' | 'exit';

export type DerivedGateModule = {
  role: DerivedGateRole;
  equipmentId: 'ENTRY-EQUIPMENT' | 'EXIT-EQUIPMENT';
  loadPortId: 'LP-ENTRY' | 'LP-EXIT';
  trackNodeId: string;
  nodeIndex: number;
  x: number;
  y: number;
  bodyX: number;
  bodyY: number;
  headingDeg: number;
  name: string;
  foupCapacity: number;
  segmentIds: string[];
  isJunction: boolean;
  annotation: TrackNodeAnnotation;
};

const BODY_NORMAL_OFFSET = 18;

/** Distance from track node to compact 2D badge center (beside the node, not on-rail). */
/** Badge center sits this far from the node along the outward normal. */
export const GATE_2D_BESIDE_NODE_OFFSET = 7.5;
export const GATE_2D_JUNCTION_BESIDE_BONUS = 2;

export function gateOutwardNormal(
  role: DerivedGateRole,
  headingDeg: number
): { x: number; y: number } {
  const headingRad = (headingDeg * Math.PI) / 180;
  const normalSign = role === 'entry' ? -1 : 1;
  return {
    x: -Math.sin(headingRad) * normalSign,
    y: Math.cos(headingRad) * normalSign,
  };
}

/** World position for the compact 2D entry/exit badge (not shared with 3D body offset). */
export function resolveGate2DGroupPosition(module: DerivedGateModule): {
  x: number;
  y: number;
} {
  const outward = gateOutwardNormal(module.role, module.headingDeg);
  const dist =
    GATE_2D_BESIDE_NODE_OFFSET +
    (module.isJunction ? GATE_2D_JUNCTION_BESIDE_BONUS : 0);
  return {
    x: module.x + outward.x * dist,
    y: module.y + outward.y * dist,
  };
}

/**
 * World Y of the Entry/Exit handoff deck, where the gate presents (or receives)
 * a FOUP and the OHT hoist meets it.
 *
 * The gate never lifts a FOUP up to the rail: it stays at load-port height and
 * the vehicle lowers onto it. Both the 3D gate geometry and the OHT hoist target
 * read this constant so the two halves of the handoff cannot drift apart.
 */
export const GATE_HANDOFF_DECK_Y = 20;

function outwardVector(edge: TrackEdge, nodeIndex: number): { x: number; y: number } | null {
  if (edge.points.length < 2) return null;
  if (edge.a === nodeIndex) {
    return {
      x: edge.points[1]!.x - edge.points[0]!.x,
      y: edge.points[1]!.y - edge.points[0]!.y,
    };
  }
  if (edge.b === nodeIndex) {
    const end = edge.points.length - 1;
    return {
      x: edge.points[end - 1]!.x - edge.points[end]!.x,
      y: edge.points[end - 1]!.y - edge.points[end]!.y,
    };
  }
  return null;
}

export function resolveGateTrackHeading(
  graph: TrackGraph,
  nodeIndex: number
): number {
  const incident = (graph.nodeEdges[nodeIndex] ?? [])
    .map((edgeIndex) => graph.edges[edgeIndex])
    .filter((edge): edge is TrackEdge => Boolean(edge))
    .sort((a, b) => b.length - a.length || a.id.localeCompare(b.id));
  for (const edge of incident) {
    const vector = outwardVector(edge, nodeIndex);
    if (!vector || Math.hypot(vector.x, vector.y) < 1e-6) continue;
    return (Math.atan2(vector.y, vector.x) * 180) / Math.PI;
  }
  return 0;
}

function positiveCapacity(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_GATE_FOUP_CAPACITY;
}

/** Resolve persistent annotations into render/runtime gate equipment modules. */
export function resolveDerivedGateModules(
  layoutElements: readonly LayoutElement[],
  annotations: readonly TrackNodeAnnotation[],
  tolerance = ENDPOINT_TOLERANCE
): DerivedGateModule[] {
  const elements = [...layoutElements];
  const graph = buildTrackGraph(elements);
  const nodes = buildSimulationTrackNodeViews(elements);
  const result: DerivedGateModule[] = [];

  for (const annotation of annotations) {
    if (annotation.role !== 'entry' && annotation.role !== 'exit') continue;
    let nodeIndex = -1;
    let bestDistance = tolerance * tolerance;
    graph.nodes.forEach((node, index) => {
      const distance =
        (node.x - annotation.x) ** 2 + (node.y - annotation.y) ** 2;
      if (distance <= bestDistance) {
        bestDistance = distance;
        nodeIndex = index;
      }
    });
    if (nodeIndex < 0) continue;
    const node = graph.nodes[nodeIndex]!;
    const role = annotation.role;
    const headingDeg = resolveGateTrackHeading(graph, nodeIndex);
    const headingRad = (headingDeg * Math.PI) / 180;
    const normalSign = role === 'entry' ? -1 : 1;
    const normalX = -Math.sin(headingRad) * normalSign;
    const normalY = Math.cos(headingRad) * normalSign;
    const view = nodes[nodeIndex];
    result.push({
      role,
      equipmentId: role === 'entry' ? 'ENTRY-EQUIPMENT' : 'EXIT-EQUIPMENT',
      loadPortId:
        role === 'entry'
          ? SYNTHETIC_ENTRY_LOAD_PORT_ID
          : SYNTHETIC_EXIT_LOAD_PORT_ID,
      trackNodeId: simulationTrackNodeId(nodeIndex),
      nodeIndex,
      x: node.x,
      y: node.y,
      bodyX: node.x + normalX * BODY_NORMAL_OFFSET,
      bodyY: node.y + normalY * BODY_NORMAL_OFFSET,
      headingDeg,
      name: annotation.name.trim() || (role === 'entry' ? 'Entry' : 'Exit'),
      foupCapacity: positiveCapacity(annotation.foupCapacity),
      segmentIds: view?.segmentIds ?? [],
      isJunction:
        annotation.isJunction === true ||
        (graph.nodeEdges[nodeIndex]?.length ?? 0) > 2,
      annotation,
    });
  }

  return result.sort((a, b) => a.role.localeCompare(b.role));
}
